import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Calendar, BarChart3, ChevronRight, Check, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoMark, LogoWordmarkReversed } from '@/components/Logo';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Home,
      title: 'Property management',
      description: 'Add and manage all your rental properties in one central location. Track status, rent, and expenses effortlessly.',
    },
    {
      icon: FileText,
      title: 'Document storage',
      description: 'Upload and organize important documents like insurance policies, inspection reports, and letting statements.',
    },
    {
      icon: Calendar,
      title: 'Key date tracking',
      description: 'Never miss an important deadline. Track insurance expiry, mortgage terms, and inspection dates with visual urgency indicators.',
    },
    {
      icon: BarChart3,
      title: 'Financial overview',
      description: 'View your total monthly rent and expenses at a glance. Understand your portfolio\'s performance instantly.',
    },
  ];

  const benefits = [
    'Centralize all property documents',
    'Track important dates and deadlines',
    'View financial summary at a glance',
    'Access from any device',
    'Secure multi-tenant data isolation',
    'Simple, intuitive interface',
  ];

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="bg-white border-b border-line sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={40} />
            <span className="text-xl font-bold font-display tracking-tight">
              <span className="text-ink">Portfolio</span>
              <span className="text-signal-blue"> Pal</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button data-testid="go-to-dashboard-btn" className="bg-anchor-blue hover:bg-anchor-blue/90">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button data-testid="login-btn" variant="ghost" className="text-muted-brand hover:text-ink">
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button data-testid="signup-btn" className="bg-anchor-blue hover:bg-anchor-blue/90">
                    Get started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-up">
              <p className="text-sm font-semibold text-signal-blue uppercase tracking-wider mb-4">
                For portfolio landlords
              </p>
              <h1 className="text-5xl lg:text-6xl font-extrabold text-ink leading-[1.1] font-display tracking-tight">
                Manage your property portfolio with ease
              </h1>
              <p className="mt-6 text-xl text-muted-brand leading-relaxed max-w-xl">
                Portfolio Pal helps landlords centralize documents, track key dates, and monitor financials - all in one simple dashboard designed for your weekly check-in.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button data-testid="hero-get-started-btn" size="lg" className="bg-anchor-blue hover:bg-anchor-blue/90 h-12 px-8 text-base rounded-xl">
                    Start free <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button data-testid="hero-login-btn" size="lg" variant="outline" className="h-12 px-8 text-base border-line hover:bg-cloud rounded-xl">
                    Log in
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in">
              <div className="absolute inset-0 bg-gradient-to-r from-signal-blue/10 to-meadow-green/10 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1617341623760-1919df79274c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwc3Vubnl8ZW58MHx8fHwxNzcxNzkwOTc1fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern apartment building"
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              {/* Accent roofline */}
              <div className="absolute -top-2 left-8 w-24 h-1 bg-sunbeam-amber rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-ink font-display tracking-tight">
              Everything you need to manage your portfolio
            </h2>
            <p className="mt-4 text-lg text-muted-brand max-w-2xl mx-auto">
              Designed specifically for portfolio landlords who want clarity without complexity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className={`p-6 rounded-2xl border transition-all duration-200 cursor-pointer ${
                  activeFeature === index
                    ? 'bg-anchor-blue border-anchor-blue text-white shadow-lg scale-[1.02]'
                    : 'bg-white border-line hover:border-signal-blue/30 hover:shadow-md'
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  activeFeature === index ? 'bg-white/20' : 'bg-signal-blue/10'
                }`}>
                  <feature.icon
                    className={`w-5 h-5 ${activeFeature === index ? 'text-white' : 'text-signal-blue'}`}
                    strokeWidth={2}
                  />
                </div>
                <h3 className={`mt-4 text-lg font-semibold font-display ${activeFeature === index ? 'text-white' : 'text-ink'}`}>
                  {feature.title}
                </h3>
                <p className={`mt-2 text-sm leading-relaxed ${activeFeature === index ? 'text-white/80' : 'text-muted-brand'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-paper">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758876020343-c8c2add9d527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxvcmdhbml6ZWQlMjBkZXNrJTIwd2l0aCUyMGRvY3VtZW50cyUyMGFuZCUyMGxhcHRvcHxlbnwwfHx8fDE3NzE3OTA5Nzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Organized desk"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              {/* Accent roofline */}
              <div className="absolute -bottom-2 right-8 w-32 h-1 bg-sunbeam-amber rounded-full"></div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-ink font-display tracking-tight">
                Built for busy landlords
              </h2>
              <p className="mt-4 text-lg text-muted-brand">
                Stop juggling spreadsheets and paper files. Portfolio Pal gives you a single source of truth for your entire portfolio.
              </p>
              <ul className="mt-8 space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-meadow-green/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-meadow-green" strokeWidth={2.5} />
                    </div>
                    <span className="text-slate-brand">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Link to="/signup">
                  <Button data-testid="benefits-get-started-btn" className="bg-anchor-blue hover:bg-anchor-blue/90 h-12 px-8 rounded-xl">
                    Get started for free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-anchor-blue">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white font-display tracking-tight">
            Ready to simplify your property management?
          </h2>
          <p className="mt-4 text-lg text-white/70">
            Join landlords who've found a better way to manage their portfolios.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button data-testid="cta-signup-btn" size="lg" className="bg-white text-anchor-blue hover:bg-white/90 h-12 px-8 text-base rounded-xl font-semibold">
                Create free account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white border-t border-line">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={32} />
            <span className="font-semibold font-display text-ink">Portfolio Pal</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-muted-brand">
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-ink transition-colors">Terms of Service</Link>
          </div>
          <p className="text-sm text-muted-brand">
            © {new Date().getFullYear()} Portfolio Pal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
